/* =========================
   PEGUU — CORREGIR DETALLE
   /pages/corregir-detalle
========================= */

(() => {
  "use strict";

  const STORE_SOURCE = "store_order";
  const OPTION_UPLOAD = "UPLOAD_NEW_FILE";
  const OPTION_REFUND = "REFUND";
  const OPTION_PAY = "PAY_DIFFERENCE";
  const OPTION_ADJUST = "ADJUST_QUANTITY";
  const CARD_ADJUST_MAX = "ADJUST_QUANTITY_MAX";
  const CARD_ADJUST_SAME = "ADJUST_QUANTITY_SAME";
  const TOKEN_STORAGE_KEY = "peguu:design-resolution-token";
  const RESOLUTION_HANDLES = [
    "corregirdetalle",
    "corregir-detalle"
  ];

  const QUALITY_CATEGORIES = ["quality", "calidad"];
  const POLICY_CATEGORIES = [
    "policies",
    "policy",
    "politicas",
    "políticas"
  ];
  const MEASURE_CATEGORIES = [
    "measures",
    "measure",
    "medidas",
    "medida",
    "cantidad",
    "quantity"
  ];

  const ALLOWED_EXTENSIONS = ["jpg", "jpeg", "png", "webp", "pdf"];
  const ALLOWED_MIME_TYPES = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "application/pdf"
  ];
  const PREVIEWABLE_EXTENSIONS = ["jpg", "jpeg", "png", "webp"];
  const MAX_FILE_SIZE = 10 * 1024 * 1024;

  const isResolutionLocation = () => {
    const path = window.location.pathname.toLowerCase();

    return RESOLUTION_HANDLES.some((handle) => path.includes(handle));
  };

  document.documentElement.classList.toggle(
    "peguu-is-design-resolution",
    isResolutionLocation()
  );

  const resolveApiUrl = (path) => {
    if (window.PeguuApi?.resolve) {
      return window.PeguuApi.resolve(path);
    }

    return path;
  };

  const firstText = (...values) => {
    for (const value of values) {
      if (value == null || typeof value === "boolean") {
        continue;
      }

      if (typeof value === "object") {
        continue;
      }

      const text = String(value).trim();

      if (text && text !== "undefined" && text !== "null") {
        return text;
      }
    }

    return "";
  };

  const asObject = (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return {};
    }

    return value;
  };

  const parseJson = async (response) => {
    const text = await response.text();

    if (!text) {
      return {};
    }

    try {
      return JSON.parse(text);
    } catch (error) {
      return {};
    }
  };

  const normalizeCategory = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const compactKey = (value) =>
    String(value || "")
      .trim()
      .toUpperCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[\s-]+/g, "_")
      .replace(/_+/g, "_");

  const optionSetFrom = (raw) => {
    const list = Array.isArray(raw) ? raw : [];
    const set = new Set();

    list.forEach((item) => {
      if (typeof item === "string" || typeof item === "number") {
        const key = compactKey(item);

        if (key) {
          set.add(key);
        }

        return;
      }

      const object = asObject(item);
      const key = compactKey(
        object.option ||
          object.type ||
          object.code ||
          object.id ||
          object.key ||
          object.name
      );

      if (key) {
        set.add(key);
      }
    });

    return set;
  };

  const toAmount = (value) => {
    if (value == null || value === "") {
      return null;
    }

    if (typeof value === "number") {
      return Number.isFinite(value) ? value : null;
    }

    const numeric = Number(String(value).replace(/[^\d.-]/g, ""));

    return Number.isFinite(numeric) ? numeric : null;
  };

  const formatMoney = (value, currency = "MXN") => {
    const amount = toAmount(value);

    if (amount == null) {
      return "";
    }

    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: firstText(currency) || "MXN"
    }).format(amount);
  };

  const extractCheckoutUrl = (...roots) => {
    for (const root of roots) {
      const data = asObject(root);
      const nested = asObject(data.data || data.checkout || data.payment);
      const url = firstText(
        data.checkoutUrl,
        data.checkout_url,
        nested.checkoutUrl,
        nested.checkout_url
      );

      if (url && /^https?:\/\//i.test(url)) {
        return url;
      }
    }

    return "";
  };

  const categoryLabel = (category) => {
    const value = normalizeCategory(category);

    if (QUALITY_CATEGORIES.includes(value)) {
      return "Calidad";
    }

    if (POLICY_CATEGORIES.includes(value)) {
      return "Políticas";
    }

    if (MEASURE_CATEGORIES.includes(value)) {
      return "Medidas";
    }

    return firstText(category) || "Revisión";
  };

  const formatQuantity = (value) => {
    const text = firstText(value);

    if (!text) {
      return "";
    }

    if (/pieza|pz/i.test(text)) {
      return text;
    }

    const numeric = Number(String(text).replace(/[^\d.-]/g, ""));

    if (!Number.isFinite(numeric) || numeric <= 0) {
      return text;
    }

    return `${numeric.toLocaleString("es-MX")} piezas`;
  };

  const formatSizePair = (width, height) => {
    const widthText = firstText(width);
    const heightText = firstText(height);

    if (!widthText || !heightText) {
      return "";
    }

    return `${widthText} × ${heightText} cm`;
  };

  const formatSize = (order) => {
    const pair = formatSizePair(
      order.widthCm || order.width || order.ancho,
      order.heightCm || order.height || order.alto
    );

    if (pair) {
      return pair;
    }

    const direct = firstText(order.tamano, order.size, order.medidas);

    if (!direct) {
      return "";
    }

    if (/cm/i.test(direct)) {
      return direct;
    }

    const parts = direct.split(/\s*[x×]\s*/i).filter(Boolean);

    if (parts.length === 2) {
      return `${parts[0]} × ${parts[1]} cm`;
    }

    return direct;
  };

  const formatExpiresAt = (value) => {
    const text = firstText(value);

    if (!text) {
      return "";
    }

    const date = new Date(text);

    if (Number.isNaN(date.getTime())) {
      return `Este enlace vence el ${text}`;
    }

    const formatted = new Intl.DateTimeFormat("es-MX", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    }).format(date);

    return `Este enlace vence el ${formatted}`;
  };

  const readStoredToken = () => {
    try {
      return firstText(sessionStorage.getItem(TOKEN_STORAGE_KEY));
    } catch (error) {
      return "";
    }
  };

  const storeToken = (token) => {
    if (!token) {
      return;
    }

    try {
      sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
    } catch (error) {
      return;
    }
  };

  const getCleanPath = () => {
    const markedPath = firstText(
      document.querySelector("[data-peguu-design-resolution]")
        ?.dataset.resolutionPath
    );

    if (markedPath) {
      return markedPath.startsWith("/") ? markedPath : `/${markedPath}`;
    }

    return "/pages/corregir-detalle";
  };

  const hideTokenFromUrl = () => {
    const url = new URL(window.location.href);

    url.searchParams.delete("token");
    url.searchParams.delete("t");
    url.hash = "";

    const segments = url.pathname
      .replace(/\/+$/, "")
      .split("/")
      .filter(Boolean);
    const handleIndex = segments.findIndex((segment) =>
      RESOLUTION_HANDLES.includes(segment.toLowerCase())
    );

    if (handleIndex >= 0) {
      url.pathname = `/${segments.slice(0, handleIndex + 1).join("/")}`;
    } else {
      url.pathname = getCleanPath();
    }

    const next = `${url.pathname}${url.search}`;
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    if (current !== next) {
      history.replaceState({}, "", next);
    }
  };

  const getTokenFromLocation = () => {
    const url = new URL(window.location.href);
    const queryToken = firstText(
      url.searchParams.get("token"),
      url.searchParams.get("t")
    );
    const hashToken = firstText(
      url.hash.replace(/^#/, "").replace(/^token=/i, "")
    );
    const segments = url.pathname
      .replace(/\/+$/, "")
      .split("/")
      .filter(Boolean);
    const handleIndex = segments.findIndex((segment) =>
      RESOLUTION_HANDLES.includes(segment.toLowerCase())
    );
    const pathToken =
      handleIndex >= 0 && segments[handleIndex + 1]
        ? decodeURIComponent(segments[handleIndex + 1])
        : "";
    const token = firstText(
      queryToken,
      pathToken,
      hashToken,
      readStoredToken()
    );

    if (token) {
      storeToken(token);
    }

    hideTokenFromUrl();

    return token;
  };

  const initialize = () => {
    if (!isResolutionLocation()) {
      return;
    }

    const root = document.querySelector("[data-peguu-design-resolution]");

    if (!root || root.dataset.initialized === "true") {
      return;
    }

    root.dataset.initialized = "true";

    const fileInput = root.querySelector("[data-design-file-input]");
    const uploadBox = root.querySelector("[data-design-upload-box]");
    const previewImage = root.querySelector("[data-design-preview-image]");
    const fileIcon = root.querySelector("[data-design-file-icon]");
    const replaceControl = root.querySelector("[data-design-replace]");
    const removeButton = root.querySelector("[data-remove-file-button]");
    const uploadStatus = root.querySelector("[data-design-upload-status]");
    const uploadButton = root.querySelector("[data-upload-confirm]");
    const actionButtons = root.querySelectorAll("[data-resolution-action]");
    const optionCards = root.querySelectorAll("[data-option-card]");
    const heading = root.querySelector("[data-order-heading]");
    const reasonsBlock = root.querySelector("[data-reasons-block]");
    const reasonsList = root.querySelector("[data-reasons-list]");
    const expiresNode = root.querySelector("[data-order-expires]");
    const detectedRow = root.querySelector('[data-order-row="detectado"]');
    const finalTitle = root.querySelector("[data-final-title]");
    const finalCopy = root.querySelector("[data-final-copy]");
    const finalCheckout = root.querySelector("[data-final-checkout]");

    const orderFields = {
      orden: root.querySelector("[data-order-orden]"),
      cliente: root.querySelector("[data-order-cliente]"),
      producto: root.querySelector("[data-order-producto]"),
      cantidad: root.querySelector("[data-order-cantidad]"),
      tamano: root.querySelector("[data-order-tamano]"),
      detectado: root.querySelector("[data-order-detectado]")
    };

    let token = "";
    let selectedFile = null;
    let previewUrl = "";
    let isBusy = false;
    let isStoreOrder = false;
    let quote = {};
    let detectedSize = { width: "", height: "" };
    let currency = "MXN";

    const setView = (view) => {
      root.dataset.view = view;
      root.querySelectorAll("[data-view-panel]").forEach((panel) => {
        panel.hidden = panel.dataset.viewPanel !== view;
      });
    };

    const setStatus = (node, message, state = "") => {
      if (!node) {
        return;
      }

      node.textContent = message;
      node.hidden = !message;
      node.dataset.state = state;
    };

    const cardFor = (key) =>
      root.querySelector(`[data-option-card="${key}"]`);

    const showFinal = ({
      title,
      copy,
      checkoutUrl = ""
    }) => {
      if (finalTitle) {
        finalTitle.textContent = title;
      }

      if (finalCopy) {
        finalCopy.textContent = copy;
      }

      if (finalCheckout) {
        if (checkoutUrl) {
          finalCheckout.hidden = false;
          finalCheckout.href = checkoutUrl;
        } else {
          finalCheckout.hidden = true;
          finalCheckout.removeAttribute("href");
        }
      }

      setView("final");
    };

    const getFileExtension = (fileName) => {
      if (!fileName || typeof fileName !== "string") {
        return "";
      }

      return fileName.split(".").pop()?.toLowerCase() || "";
    };

    const validateFile = (file) => {
      if (!file) {
        return {
          valid: false,
          message: "No se seleccionó ningún archivo."
        };
      }

      const extension = getFileExtension(file.name);
      const mimeType = String(file.type || "").toLowerCase();

      if (!extension || !ALLOWED_EXTENSIONS.includes(extension)) {
        return {
          valid: false,
          message: "Formato no válido. Usa JPEG, PNG, WEBP o PDF."
        };
      }

      if (mimeType && !ALLOWED_MIME_TYPES.includes(mimeType)) {
        return {
          valid: false,
          message: "Tipo de archivo no permitido. Usa JPEG, PNG, WEBP o PDF."
        };
      }

      if (file.size > MAX_FILE_SIZE) {
        return {
          valid: false,
          message: "El archivo supera el tamaño máximo permitido de 10 MB."
        };
      }

      return {
        valid: true,
        message: ""
      };
    };

    const clearPreviewUrl = () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        previewUrl = "";
      }
    };

    const renderPreview = (file) => {
      const extension = getFileExtension(file?.name);
      const showImage = PREVIEWABLE_EXTENSIONS.includes(extension);

      if (previewImage) {
        previewImage.hidden = !showImage;
        previewImage.src = showImage ? previewUrl : "";
        previewImage.alt = showImage
          ? `Previsualización de ${file.name}`
          : "";
      }

      if (fileIcon) {
        fileIcon.hidden = !file || showImage;
        fileIcon.textContent = String(extension || "ARCHIVO").toUpperCase();
      }
    };

    const setUploadBoxLabel = (text) => {
      const textElement = uploadBox?.querySelector(
        ".peguu-design-resolution__upload-text"
      );

      if (textElement) {
        textElement.textContent = text;
      }
    };

    const setFilledState = (isFilled) => {
      uploadBox?.classList.toggle("is-filled", isFilled);

      if (replaceControl) {
        replaceControl.hidden = !isFilled;
      }

      if (removeButton) {
        removeButton.hidden = !isFilled;
      }
    };

    const clearSelectedFile = () => {
      selectedFile = null;

      if (fileInput) {
        fileInput.value = "";
      }

      clearPreviewUrl();
      setUploadBoxLabel("SELECCIONA UN ARCHIVO");
      setFilledState(false);
      renderPreview(null);
    };

    const defaultActionLabel = (key) => {
      if (key === OPTION_REFUND) {
        return "Pedir reembolso mayor";
      }

      if (key === OPTION_PAY) {
        return "Pagar diferencia";
      }

      if (key === CARD_ADJUST_MAX) {
        return "Bajar piezas";
      }

      if (key === CARD_ADJUST_SAME) {
        return "Dejar las mismas piezas";
      }

      return "Continuar";
    };

    const updateButtons = () => {
      if (uploadButton) {
        uploadButton.disabled = isBusy || !selectedFile;
        uploadButton.textContent = isBusy
          ? "Enviando…"
          : "Enviar nuevo diseño";
      }

      actionButtons.forEach((button) => {
        const key = firstText(button.dataset.resolutionAction);
        const busyLabel =
          key === OPTION_PAY ? "Abriendo pago…" : "Enviando…";

        button.disabled = isBusy;
        button.textContent = isBusy
          ? busyLabel
          : defaultActionLabel(key);
      });

      uploadBox?.classList.toggle("is-disabled", isBusy);

      if (fileInput) {
        fileInput.disabled = isBusy;
      }
    };

    const byTokenUrl = (suffix = "") => {
      const encodedToken = encodeURIComponent(token);
      const extra = suffix ? `/${suffix.replace(/^\/+/, "")}` : "";

      return resolveApiUrl(
        `/api/design-resolution/by-token/${encodedToken}${extra}`
      );
    };

    const postJson = async (path, body) => {
      const response = await fetch(byTokenUrl(path), {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify(body)
      });
      const payload = await parseJson(response);

      if (!response.ok || payload?.success === false) {
        throw new Error(
          firstText(payload.error, payload.message) ||
            "No pudimos completar la acción. Inténtalo de nuevo."
        );
      }

      return payload;
    };

    const payloadData = (payload) =>
      asObject(payload.data || payload);

    const readDetectedSize = (data) => {
      const caseInfo = asObject(data.case);
      const size = asObject(caseInfo.detectedSizeCm);
      const quoteData = asObject(data.quote);

      return {
        width: firstText(
          quoteData.detectedWidthCm,
          size.width,
          size.widthCm
        ),
        height: firstText(
          quoteData.detectedHeightCm,
          size.height,
          size.heightCm
        )
      };
    };

    const paintOrder = (data) => {
      const order = asObject(data.orderBefore);
      const values = {
        orden: firstText(order.orden, order.orderNumber, order.odt),
        cliente: firstText(order.cliente, order.guestName),
        producto: firstText(order.producto, order.product),
        cantidad: formatQuantity(
          quote.originalQuantity || order.quantity || order.cantidad
        ),
        tamano: formatSize(order),
        detectado: formatSizePair(detectedSize.width, detectedSize.height)
      };

      Object.entries(values).forEach(([key, value]) => {
        if (orderFields[key]) {
          orderFields[key].textContent = value || "—";
        }
      });

      if (detectedRow) {
        detectedRow.hidden = !values.detectado;
      }

      if (heading) {
        const odt = firstText(order.odt, values.orden);
        const client = values.cliente;

        heading.innerHTML =
          odt && client
            ? `Pedido <strong>${odt} — ${client}</strong>`
            : "Revisa tu pedido y elige cómo continuar.";
      }

      const reasons = Array.isArray(order.reasons) ? order.reasons : [];

      if (reasonsList) {
        reasonsList.innerHTML = "";

        reasons.forEach((reason) => {
          const item = document.createElement("li");
          const label = categoryLabel(reason?.category);
          const detail = firstText(
            reason?.reason,
            reason?.message,
            reason?.detail
          );
          item.textContent = detail ? `${label}: ${detail}` : label;
          reasonsList.appendChild(item);
        });
      }

      if (reasonsBlock) {
        reasonsBlock.hidden = reasons.length === 0;
      }

      const expiresText = formatExpiresAt(
        firstText(asObject(data.case).expiresAt, data.expiresAt)
      );

      if (expiresNode) {
        expiresNode.textContent = expiresText;
        expiresNode.hidden = !expiresText;
      }
    };

    const paintCardCopy = (key, copy, amount) => {
      const card = cardFor(key);

      if (!card) {
        return;
      }

      const copyNode = card.querySelector("[data-option-copy]");
      const amountNode = card.querySelector("[data-option-amount]");

      if (copyNode && copy) {
        copyNode.textContent = copy;
      }

      if (amountNode) {
        const money = formatMoney(amount, currency);
        amountNode.textContent = money;
        amountNode.hidden = !money;
      }
    };

    const applyVisibleCards = (visibleKeys) => {
      const keys = new Set(visibleKeys);

      optionCards.forEach((card) => {
        card.hidden = !keys.has(card.dataset.optionCard);
      });

      [...optionCards]
        .filter((card) => !card.hidden)
        .forEach((card, index) => {
          const kicker = card.querySelector("[data-option-kicker]");

          if (kicker) {
            kicker.textContent = `Opción ${index + 1}`;
          }
        });
    };

    const visibleCardsFromData = (data) => {
      const options = optionSetFrom(data.options || asObject(data.case).options);
      const visible = [];
      const option2 = quote.option2 == null ? null : asObject(quote.option2);
      const hasOption2 = isStoreOrder && data.quote != null && quote.option2 != null;
      const difference = isStoreOrder ? toAmount(quote.priceDifferenceWithIva) : null;
      const order = asObject(data.orderBefore);
      const paidAmount = isStoreOrder
        ? toAmount(quote.paidAmountWithIva)
        : toAmount(order.lineTotal) ??
          toAmount(order.amount) ??
          toAmount(order.monto);

      if (options.has(OPTION_UPLOAD)) {
        visible.push(OPTION_UPLOAD);
      }

      if (options.has(OPTION_REFUND)) {
        visible.push(OPTION_REFUND);
        paintCardCopy(
          OPTION_REFUND,
          paidAmount != null
            ? `Reembolso de lo pagado por este producto (${formatMoney(paidAmount, currency)}).`
            : "Reembolso de lo pagado por este producto.",
          paidAmount
        );
      }

      if (options.has(OPTION_PAY) && isStoreOrder && difference != null && difference > 0) {
        visible.push(OPTION_PAY);
        paintCardCopy(
          OPTION_PAY,
          `Pagar ${formatMoney(difference, currency)} más y producir ${formatQuantity(quote.originalQuantity)} a ${formatSizePair(detectedSize.width, detectedSize.height) || "las medidas detectadas"}.`,
          difference
        );
      }

      if (options.has(OPTION_ADJUST) && hasOption2) {
        visible.push(CARD_ADJUST_MAX);
        paintCardCopy(
          CARD_ADJUST_MAX,
          `Imprimir hasta ${formatQuantity(option2.maxPieces)}. Reembolso ${formatMoney(option2.refundWithIva, currency) || "$0.00"}.`,
          option2.refundWithIva
        );
      }

      if (options.has(OPTION_ADJUST) && isStoreOrder && difference != null && difference < 0) {
        visible.push(CARD_ADJUST_SAME);
        paintCardCopy(
          CARD_ADJUST_SAME,
          `Dejar ${formatQuantity(quote.originalQuantity)} y reembolsar ${formatMoney(Math.abs(difference), currency)}.`,
          Math.abs(difference)
        );
      }

      return visible;
    };

    const showFormWithCards = (data, onlyUpload = false) => {
      paintOrder(data);

      const visible = onlyUpload
        ? [OPTION_UPLOAD]
        : visibleCardsFromData(data);

      applyVisibleCards(visible);
      setView("form");
      updateButtons();
    };

    const showStatusFromCase = (data) => {
      const caseInfo = asObject(data.case);
      const status = compactKey(caseInfo.status);
      const selected = compactKey(caseInfo.selectedOption);
      const checkoutUrl = isStoreOrder
        ? extractCheckoutUrl(data, caseInfo)
        : "";

      if (status === "OPEN") {
        const visible = visibleCardsFromData(data);

        if (!visible.length) {
          showFinal({
            title: "Este enlace no está disponible",
            copy: "Este caso no tiene opciones disponibles por ahora."
          });
          return;
        }

        showFormWithCards(data);
        return;
      }

      if (status === "SELECTED" && selected === OPTION_PAY && checkoutUrl) {
        showFinal({
          title: "Termina el pago",
          copy: "Ya elegiste pagar la diferencia. Completa el cobro en Shopify para continuar tu pedido.",
          checkoutUrl
        });
        return;
      }

      if (status === "SELECTED" && selected === OPTION_REFUND) {
        showFinal({
          title: "Estamos procesando tu reembolso",
          copy: "Shopify aún está pendiente. Te depositará por el mismo método de pago."
        });
        return;
      }

      if (status === "SELECTED" && selected === OPTION_UPLOAD) {
        showFormWithCards(data, true);
        return;
      }

      if (status === "SELECTED" && selected === OPTION_ADJUST) {
        showFinal({
          title: "Reembolso en proceso",
          copy: "Recibimos tu ajuste. El reembolso menor se completará en breve."
        });
        return;
      }

      if (status === "RESOLVED" && selected === OPTION_REFUND) {
        showFinal({
          title: "Reembolso listo",
          copy: "Reembolso hecho. Shopify te lo deposita por el mismo método de pago."
        });
        return;
      }

      if (status === "RESOLVED" && selected === OPTION_PAY) {
        showFinal({
          title: "Pago extra aplicado",
          copy: "El pago adicional quedó aplicado y tu pedido continúa."
        });
        return;
      }

      if (status === "RESOLVED" && selected === OPTION_ADJUST) {
        showFinal({
          title: "Pedido ajustado",
          copy: "Cantidad y medidas quedaron actualizadas. El reembolso menor ya se hizo."
        });
        return;
      }

      if (status === "RESOLVED" && selected === OPTION_UPLOAD) {
        showFinal({
          title: "Diseño enviado",
          copy: "Diseño enviado, lo volvemos a revisar."
        });
        return;
      }

      if (status === "EXPIRED") {
        showFinal({
          title: "Enlace vencido",
          copy: "Este enlace ya venció y no se puede usar."
        });
        return;
      }

      showFinal({
        title: "Este enlace no está disponible",
        copy: "El enlace para corregir el diseño está usado, vencido o inactivo."
      });
    };

    const loadCase = async () => {
      token = getTokenFromLocation();

      if (!token) {
        showFinal({
          title: "Este enlace no está disponible",
          copy: "No encontramos el enlace de esta revisión. Revisa el correo o solicita uno nuevo."
        });
        return;
      }

      try {
        const response = await fetch(byTokenUrl(), {
          method: "GET",
          headers: {
            Accept: "application/json"
          }
        });
        const payload = await parseJson(response);
        const data = asObject(payload.data || payload);

        if (!response.ok || payload?.success === false) {
          showFinal({
            title: "Este enlace no está disponible",
            copy:
              firstText(payload.error, payload.message) ||
              "El enlace para corregir el diseño está usado, vencido o inactivo."
          });
          return;
        }

        const caseInfo = asObject(data.case);

        isStoreOrder =
          firstText(data.source, caseInfo.source).toLowerCase() ===
          STORE_SOURCE;
        quote = isStoreOrder ? asObject(data.quote) : {};
        detectedSize = readDetectedSize(data);
        currency =
          firstText(
            data.currency,
            caseInfo.currency,
            asObject(data.orderBefore).currency,
            "MXN"
          ) || "MXN";

        showStatusFromCase(data);
      } catch (error) {
        showFinal({
          title: "No pudimos cargar la revisión",
          copy: "Inténtalo más tarde."
        });
      }
    };

    const handleSelectedFile = (file) => {
      const validation = validateFile(file);

      if (!validation.valid) {
        setStatus(uploadStatus, validation.message, "error");

        if (fileInput) {
          fileInput.value = "";
        }

        updateButtons();
        return;
      }

      selectedFile = file;
      clearPreviewUrl();

      if (PREVIEWABLE_EXTENSIONS.includes(getFileExtension(file.name))) {
        previewUrl = URL.createObjectURL(file);
      }

      setUploadBoxLabel(file.name);
      setFilledState(true);
      renderPreview(file);
      setStatus(uploadStatus, "Archivo listo para enviar.", "success");
      updateButtons();
    };

    const handleUpload = async () => {
      if (isBusy || !selectedFile) {
        if (!selectedFile) {
          setStatus(
            uploadStatus,
            "Selecciona un archivo para continuar.",
            "error"
          );
        }

        return;
      }

      isBusy = true;
      updateButtons();
      setStatus(uploadStatus, "Subiendo archivo…", "loading");

      try {
        try {
          await postJson("select", { option: OPTION_UPLOAD });
        } catch (selectError) {
          /* upload-new-design puede marcar la opción solo */
        }

        const formData = new FormData();
        formData.append("file", selectedFile);

        const uploadHeaders = {
          Accept: "application/json"
        };

        const uploadResponse = await fetch(
          resolveApiUrl("/api/upload/design"),
          {
            method: "POST",
            headers: uploadHeaders,
            body: formData
          }
        );
        const uploadPayload = await parseJson(uploadResponse);
        const uploadData = asObject(uploadPayload.data || uploadPayload);

        if (!uploadResponse.ok || uploadPayload?.success === false) {
          throw new Error(
            firstText(uploadPayload.error, uploadPayload.message) ||
              "No pudimos cargar el archivo. Inténtalo de nuevo."
          );
        }

        const fileId = firstText(
          uploadData.fileId,
          uploadData.file_id,
          asObject(uploadData.file).fileId
        );
        const designUrl = firstText(
          uploadData.diseno,
          uploadData.url,
          uploadData.fileUrl,
          asObject(uploadData.file).url
        );
        const body = {};

        if (designUrl) {
          body.diseno = designUrl;
        } else if (fileId) {
          body.fileId = fileId;
        }

        if (!body.fileId && !body.diseno) {
          throw new Error(
            "La respuesta de carga no incluyó el archivo para ligar el diseño."
          );
        }

        setStatus(uploadStatus, "Enviando diseño…", "loading");
        await postJson("upload-new-design", body);
        showFinal({
          title: "Diseño enviado",
          copy: "Diseño enviado, lo volvemos a revisar."
        });
      } catch (error) {
        setStatus(
          uploadStatus,
          error?.message ||
            "No pudimos enviar el diseño. Inténtalo de nuevo.",
          "error"
        );
      } finally {
        isBusy = false;
        updateButtons();
      }
    };

    const handleRefund = async () => {
      if (isBusy) {
        return;
      }

      const statusNode = cardFor(OPTION_REFUND)?.querySelector(
        "[data-option-status]"
      );

      isBusy = true;
      updateButtons();
      setStatus(statusNode, "Enviando solicitud…", "loading");

      try {
        let payload;

        try {
          payload = await postJson("select", { option: OPTION_REFUND });
        } catch (selectError) {
          payload = await postJson("confirm", { option: OPTION_REFUND });
        }

        const data = payloadData(payload);
        const status = compactKey(data.status || data.caseStatus);
        const refundPreview = asObject(data.refundPreview);

        if (status === "RESOLVED" && compactKey(refundPreview.status) === "REFUNDED") {
          showFinal({
            title: "Reembolso listo",
            copy: "Reembolso hecho. Shopify te lo deposita por el mismo método de pago."
          });
          return;
        }

        showFinal({
          title: "Solicitud registrada",
          copy: firstText(data.refundError)
            ? "Solicitud registrada; el reembolso se completará en breve."
            : "Estamos procesando tu reembolso. Shopify te lo depositará por el mismo método de pago."
        });
      } catch (error) {
        setStatus(
          statusNode,
          error?.message ||
            "No pudimos pedir el reembolso. Inténtalo de nuevo.",
          "error"
        );
      } finally {
        isBusy = false;
        updateButtons();
      }
    };

    const handlePayDifference = async () => {
      if (isBusy) {
        return;
      }

      const statusNode = cardFor(OPTION_PAY)?.querySelector(
        "[data-option-status]"
      );

      isBusy = true;
      updateButtons();
      setStatus(statusNode, "Preparando el cobro…", "loading");

      try {
        const payload = await postJson("confirm", {
          option: OPTION_PAY,
          orderUpdate: {
            widthCm: Number(detectedSize.width) || detectedSize.width,
            heightCm: Number(detectedSize.height) || detectedSize.height,
            amount: quote.newPriceWithIva
          }
        });
        const data = payloadData(payload);
        const status = compactKey(data.status || data.caseStatus);
        const checkoutUrl = extractCheckoutUrl(data, payload);

        if (checkoutUrl) {
          window.location.href = checkoutUrl;
          return;
        }

        if (status === "RESOLVED") {
          showFinal({
            title: "Pago extra aplicado",
            copy: "El pago adicional quedó aplicado y tu pedido continúa."
          });
          return;
        }

        throw new Error(
          firstText(payload.error, payload.message, data.error) ||
            "No recibimos la URL de pago. Inténtalo de nuevo."
        );
      } catch (error) {
        setStatus(
          statusNode,
          error?.message ||
            "No pudimos iniciar el cobro de la diferencia. Inténtalo de nuevo.",
          "error"
        );
      } finally {
        isBusy = false;
        updateButtons();
      }
    };

    const handleAdjustQuantity = async (variant) => {
      if (isBusy) {
        return;
      }

      const statusNode = cardFor(variant)?.querySelector(
        "[data-option-status]"
      );
      const option2 = asObject(quote.option2);
      const orderUpdate =
        variant === CARD_ADJUST_MAX
          ? {
              widthCm: Number(detectedSize.width) || detectedSize.width,
              heightCm: Number(detectedSize.height) || detectedSize.height,
              quantity: option2.maxPieces,
              amount: option2.totalWithIva
            }
          : {
              widthCm: Number(detectedSize.width) || detectedSize.width,
              heightCm: Number(detectedSize.height) || detectedSize.height,
              quantity: quote.originalQuantity,
              amount: quote.newPriceWithIva
            };

      isBusy = true;
      updateButtons();
      setStatus(statusNode, "Enviando solicitud…", "loading");

      try {
        const payload = await postJson("confirm", {
          option: OPTION_ADJUST,
          orderUpdate
        });
        const data = payloadData(payload);
        const status = compactKey(data.status || data.caseStatus);

        if (status === "RESOLVED") {
          showFinal({
            title: "Pedido ajustado",
            copy: "Cantidad y medidas quedaron actualizadas. El reembolso menor ya se hizo."
          });
          return;
        }

        showFinal({
          title: "Reembolso en proceso",
          copy: firstText(data.refundError)
            ? "Reembolso en proceso."
            : "Recibimos tu ajuste. El reembolso menor se completará en breve."
        });
      } catch (error) {
        setStatus(
          statusNode,
          error?.message ||
            "No pudimos ajustar el pedido. Inténtalo de nuevo.",
          "error"
        );
      } finally {
        isBusy = false;
        updateButtons();
      }
    };

    fileInput?.addEventListener("change", () => {
      const file = fileInput.files?.[0] || null;

      if (file) {
        handleSelectedFile(file);
      }
    });

    removeButton?.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (isBusy) {
        return;
      }

      clearSelectedFile();
      setStatus(uploadStatus, "", "");
      updateButtons();
    });

    uploadButton?.addEventListener("click", (event) => {
      event.preventDefault();
      void handleUpload();
    });

    actionButtons.forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();

        const key = firstText(button.dataset.resolutionAction);

        if (key === OPTION_REFUND) {
          void handleRefund();
          return;
        }

        if (key === OPTION_PAY) {
          void handlePayDifference();
          return;
        }

        if (key === CARD_ADJUST_MAX || key === CARD_ADJUST_SAME) {
          void handleAdjustQuantity(key);
        }
      });
    });

    ["dragenter", "dragover"].forEach((eventName) => {
      uploadBox?.addEventListener(eventName, (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (!isBusy) {
          uploadBox.classList.add("is-dragging");
        }
      });
    });

    ["dragleave", "drop"].forEach((eventName) => {
      uploadBox?.addEventListener(eventName, (event) => {
        event.preventDefault();
        event.stopPropagation();
        uploadBox.classList.remove("is-dragging");
      });
    });

    uploadBox?.addEventListener("drop", (event) => {
      if (isBusy) {
        return;
      }

      const file = event.dataTransfer?.files?.[0];

      if (file) {
        handleSelectedFile(file);
      }
    });

    void loadCase();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize, {
      once: true
    });
  } else {
    initialize();
  }
})();
